import type { LucideIcon } from 'lucide-react'
import { HeartPulse, Scale, Thermometer, TrendingDown, TrendingUp, Wind } from 'lucide-react'

import { type RegionId, temperatureDisplay, weightDisplay } from '@snootsvet/shared'

import { FormattedDate } from '@/frontend/components/formatted-datetime'
import { useViewerRegion } from '@/frontend/features/region/region-context'
import { cn } from '@/frontend/lib/utils'

type VitalsVariant =
  | 'weight'
  | 'temperature'
  | 'heartRate'
  | 'respiratoryRate'
  | 'bodyConditionScore'
  | 'painScore'

const VARIANT_CONFIG: Record<VitalsVariant, { icon?: LucideIcon; abbr?: string }> = {
  weight: { icon: Scale },
  temperature: { icon: Thermometer },
  heartRate: { icon: HeartPulse },
  respiratoryRate: { icon: Wind },
  bodyConditionScore: { abbr: 'BCS' },
  painScore: { abbr: 'Pain' },
}

/** A single vital reading chip. */
export function VitalsChip({
  variant,
  value,
  trend,
  recordedAt,
  region: regionProp,
  onClick,
  className,
}: {
  variant: VitalsVariant
  value: number
  trend?: 'up' | 'down' | 'stable' | null
  recordedAt?: string
  region?: RegionId
  onClick?: () => void
  className?: string
}) {
  const { region: viewerRegion } = useViewerRegion()
  const region = regionProp ?? viewerRegion

  const config = VARIANT_CONFIG[variant]
  const formatted = formatVital(variant, value, region)

  const Tag = onClick ? 'button' : 'div'

  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={cn(
        'basis-32 flex flex-col shrink items-center justify-center rounded-lg bg-muted/60 px-3 py-2.5 transition-colors hover:bg-muted/80',
        onClick && 'cursor-pointer',
        !onClick && 'cursor-default',
        className
      )}
    >
      <div className="flex items-center gap-1.5">
        {config.icon ? (
          <config.icon className="size-3.5 shrink-0 text-muted-foreground" />
        ) : config.abbr ? (
          <span className="text-xs font-semibold text-muted-foreground">{config.abbr}</span>
        ) : null}
        <span className="text-sm font-semibold">
          {formatted.value}
          {formatted.unit && <span className="ml-0.5">{formatted.unit}</span>}
        </span>
        {trend === 'up' && <TrendingUp className="text-primary size-3 text-muted-foreground" />}
        {trend === 'down' && <TrendingDown className="text-primary size-3 text-muted-foreground" />}
      </div>
      {recordedAt && (
        <span className="text-2xs text-muted-foreground">
          <FormattedDate value={recordedAt} clinicId={null} />
        </span>
      )}
    </Tag>
  )
}

function formatVital(variant: VitalsVariant, value: number, region: RegionId) {
  switch (variant) {
    case 'weight': {
      const w = weightDisplay(value, region)
      return { value: w.value, unit: w.unit + (w.alt ? ` (${w.alt})` : '') }
    }
    case 'temperature': {
      const t = temperatureDisplay(value, region)
      return { value: t.value, unit: t.unit }
    }
    case 'heartRate':
      return { value: String(value), unit: 'bpm' }
    case 'respiratoryRate':
      return { value: String(value), unit: 'rpm' }
    case 'bodyConditionScore':
      return { value: `${value}/9`, unit: '' }
    case 'painScore':
      return { value: `${value}/10`, unit: '' }
  }
}

const VITAL_KEYS: VitalsVariant[] = [
  'weight',
  'temperature',
  'heartRate',
  'respiratoryRate',
  'bodyConditionScore',
  'painScore',
]

/** Renders chips for all present vitals in a record. */
export function VitalsChips({
  vitals,
  recordedAt,
}: {
  vitals: Partial<Record<VitalsVariant, string | number | null>>
  recordedAt?: string
}) {
  return (
    <>
      {VITAL_KEYS.map((key) => {
        const raw = vitals[key]
        if (raw == null) return null
        return (
          <VitalsChip
            key={key}
            variant={key}
            value={typeof raw === 'string' ? Number(raw) : raw}
            recordedAt={recordedAt}
          />
        )
      })}
    </>
  )
}
